import { getSlots, setSlot } from '@/api/networkFixer'
import { CHANNEL_TYPES, CONNECTOR_TYPES } from '@/vars/general'
import { Project } from '@root/src/types/main'
import {
    getFreeMaxBusinessSlots,
    getFreeSlotsInFillOrder,
} from '@/helpers/channels/channelSlots'

export {
    buildInactiveSlotConnectItem,
    getFirstAvailableSlot,
    getFirstFreeMaxSlotId,
    getFirstFreeSlotId,
    hasAvailableChannelSlots,
    isMaxBusinessSlotType,
} from '@/helpers/channels/channelSlots'

const CHANNEL_TYPE_VALUES = Object.values(CHANNEL_TYPES)

function getConnectorId(connector: { connectorId?: string; id?: string } | null | undefined): string | null {
    return connector?.connectorId || connector?.id || null
}

function isMaxBusinessConnector(connector: { connectorType?: string } | null | undefined): boolean {
    return connector?.connectorType === CHANNEL_TYPES.maxBusiness
}

function sortConnectorsByCreatedAt<T extends { connectorId?: string; id?: string; createdAt?: number }>(
    connectors: T[],
): T[] {
    return [...connectors].sort((a, b) => {
        const aTime = a?.createdAt && a.createdAt > 0 ? a.createdAt : Number.MAX_SAFE_INTEGER
        const bTime = b?.createdAt && b.createdAt > 0 ? b.createdAt : Number.MAX_SAFE_INTEGER
        if (aTime !== Number.MAX_SAFE_INTEGER && bTime !== Number.MAX_SAFE_INTEGER) return aTime - bTime
        if (aTime === Number.MAX_SAFE_INTEGER && bTime !== Number.MAX_SAFE_INTEGER) return 1
        if (aTime !== Number.MAX_SAFE_INTEGER && bTime === Number.MAX_SAFE_INTEGER) return -1
        return String(a?.connectorId || '').localeCompare(String(b?.connectorId || ''))
    })
}

/**
 * Размещает канальные коннекторы проекта в свободные слоты по порядку.
 */
export async function assignChannelConnectorsToFreeSlots({
    projectId,
    connectors,
}: {
    projectId: string
    connectors: Array<{ connectorId?: string; id?: string; connectorType?: string; isChisai?: boolean; createdAt?: number }>
}): Promise<void> {
    const channelConnectors = sortConnectorsByCreatedAt(
        (connectors || []).filter(
            (c) =>
                c &&
                !c.isChisai &&
                CHANNEL_TYPE_VALUES.includes(c.connectorType as (typeof CHANNEL_TYPE_VALUES)[number]) &&
                getConnectorId(c),
        ),
    )

    if (channelConnectors.length === 0) {
        return
    }

    const { data: slots, error } = await getSlots({ projectId })
    if (error || !Array.isArray(slots) || slots.length === 0) {
        return
    }

    const assignedConnectorIds = new Set(
        slots
            .filter((slot) => slot?.channelConnectorId)
            .map((slot) => String(slot.channelConnectorId)),
    )

    const unassignedConnectors = channelConnectors.filter((connector) => {
        const connectorId = getConnectorId(connector)
        return connectorId && !assignedConnectorIds.has(String(connectorId))
    })

    if (unassignedConnectors.length === 0) {
        return
    }

    const regularConnectors = unassignedConnectors.filter((connector) => !isMaxBusinessConnector(connector))
    const maxBusinessConnectors = unassignedConnectors.filter(isMaxBusinessConnector)

    await bindConnectorsToSlots(regularConnectors, getFreeSlotsInFillOrder(slots))
    await bindConnectorsToSlots(maxBusinessConnectors, getFreeMaxBusinessSlots(slots))
}

async function bindConnectorsToSlots(
    connectors: Array<{ connectorId?: string; id?: string }>,
    slots: Array<{ id?: string }>,
): Promise<void> {
    const assignmentsCount = Math.min(connectors.length, slots.length)

    for (let i = 0; i < assignmentsCount; i++) {
        const connectorId = getConnectorId(connectors[i])
        const slotId = slots[i]?.id
        if (!connectorId || !slotId) continue

        const { error: slotError } = await setSlot({
            slotId: slotId,
            channelConnectorId: connectorId,
        })

        if (slotError) {
            console.error('Ошибка привязки канала к слоту:', slotError)
        }
    }
}

/**
 * Обновляет проект и привязывает канальные коннекторы к свободным слотам.
 * Вызывать после успешной авторизации канала (онбординг / редирект на проект).
 */
export async function refreshProjectAndAssignChannelSlots({
    store,
    projectId,
    extraConnectors = [],
}: {
    store: {
        dispatch: (type: string, payload?: unknown) => Promise<unknown>
        getters: { projectById?: (id: string) => { connectors?: unknown[] } | null }
    }
    projectId: string
    extraConnectors?: Array<{
        connectorId?: string
        id?: string
        connectorType?: string
        isChisai?: boolean
        createdAt?: number
    }>
}): Promise<void> {
    if (!projectId || !store) return

    try {
        await store.dispatch('project/fetchProject', projectId)
    } catch (e) {
        // ignore: используем текущее состояние / extraConnectors
    }

    const project = store.getters.projectById?.(projectId)
    const connectors = [
        ...((project?.connectors || []) as Array<{
            connectorId?: string
            id?: string
            connectorType?: string
            isChisai?: boolean
            createdAt?: number
        }>),
    ]

    for (const extra of extraConnectors) {
        const extraId = getConnectorId(extra)
        if (!extraId) continue
        if (connectors.some((c) => getConnectorId(c) === extraId)) continue
        connectors.push(extra)
    }

    await assignChannelConnectorsToFreeSlots({ projectId, connectors })
}

export type OnboardingConnectionType =
    | 'yc-marketplace'
    | 'dd-marketplace'
    | 'ag-marketplace'
    | 'other-crm'

/**
 * Определяет тип подключения для онбординга
 */
export function getOnboardingConnectionType(project: Project): OnboardingConnectionType {
    if (!project?.connectors) {
        return 'other-crm'
    }

    // Проверяем YClients из маркетплейса
    const ycConnector = project.connectors.find(
        (c) => c.connectorType === CONNECTOR_TYPES.yClients && c.fromMarketplace === true
    )
    if (ycConnector) {
        return 'yc-marketplace'
    }

    // Проверяем Altegio из маркетплейса
    const altegioConnector = project.connectors.find(
        (c) => c.connectorType === CONNECTOR_TYPES.altegio && c.fromMarketplace === true
    )
    if (altegioConnector) {
        return 'ag-marketplace'
    }

    // Проверяем Dikidi из маркетплейса
    const dikidiConnector = project.connectors.find(
        (c) => c.connectorType === CONNECTOR_TYPES.dikidi && c.fromMarketplace === true
    )
    if (dikidiConnector) {
        return 'dd-marketplace'
    }

    return 'other-crm'
}

/**
 * Проверяет, является ли проект подключенным через маркетплейс
 */
export function isMarketplaceConnection(project: Project): boolean {
    const connectionType = getOnboardingConnectionType(project)
    return (
        connectionType === 'yc-marketplace' ||
        connectionType === 'dd-marketplace' ||
        connectionType === 'ag-marketplace'
    )
}



